import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { deviceTokenService } from '~/services/auth/DeviceToken';
// Módulo-folha sem imports (desenhado pra evitar ciclo) — seguro importar aqui.
import { hostApiUrl, hostTenantSlug } from '~/services/embedded/hostConfig';

// Embedded: a URL que o APK host passou vence a do build (vide hostConfig).
// Fora do embedded a expressão é a de sempre — inclusive o default 3001, que é
// do serviço legado e NÃO deve virar 8082 por engano.
const API_URL =
  hostApiUrl() ?? (import.meta.env.VITE_API_URL || 'http://localhost:3001');

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    if (new URL(config.url ?? '', API_URL).origin !== new URL(API_URL).origin) {
      throw new Error(
        'Authenticated API requests must use the configured API origin',
      );
    }
    // Add auth token if available (user auth)
    const token = localStorage.getItem('@efflux:token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add X-Device-Id header (REQUIRED for player config/ack)
    // We try to get it from localStorage which is the source of truth for AuthStore
    const deviceId = localStorage.getItem('efflux_device_id');
    if (deviceId && config.headers) {
      config.headers['X-Device-Id'] = deviceId;
    }

    // All device endpoints authenticate the stable credential bound at enrollment.
    try {
      const deviceToken = deviceTokenService.getToken();
      if (deviceToken && config.headers && !config.headers['X-Device-Token']) {
        config.headers['X-Device-Token'] = deviceToken;
      }
    } catch {
      // localStorage indisponível em alguns ambientes (SSR/test) — ignora
    }

    // Add X-Tenant-Slug header (Fixed: Resolve tenant correctly when using IP address)
    const tenantSlug = hostTenantSlug() ?? import.meta.env.VITE_TENANT_SLUG;
    if (tenantSlug && config.headers) {
      config.headers['X-Tenant-Slug'] = tenantSlug;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Regex que casa endpoints player-scoped onde 404 indica playerId stale.
// Casos cobertos:
//   GET  /api/players/{uuid}
//   POST /api/players/{uuid}/kiosk-unlock-attempt
//   POST /api/config/player/{uuid}/ack
// NÃO cobre /api/conteudos/{id} (404 ali é content not found, não player stale)
// nem /api/v1/sync/media-library (sync nunca retorna 404).
const STALE_PLAYER_ID_PATTERN =
  /\/api\/(players|config\/player)\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Authentication failures require authenticated recovery; never replace a credential
    // or retry with a new identity merely because a request returned 401.
    if (error.response?.status === 401) {
      localStorage.removeItem('@efflux:token');
      return Promise.reject(error);
    }

    // Handle 404 em endpoints player-scoped — playerId em localStorage
    // referencia player que não existe mais no servidor (deletado pelo admin
    // OU re-registrado com novo UUID). Sem isso o cliente ficaria em loop
    // sem chamar sync, mostrando "Aguardando conteúdo" indefinidamente.
    // Dynamic import pra evitar ciclo com stores (que importam request).
    if (
      error.response?.status === 404 &&
      typeof originalRequest?.url === 'string' &&
      STALE_PLAYER_ID_PATTERN.test(originalRequest.url)
    ) {
      console.warn(
        `[API] 404 em endpoint player-scoped (${originalRequest.url}) — playerId stale, disparando recovery`,
      );
      import('~/stores')
        .then(({ authStore }) => {
          authStore.handleStalePlayerId(`HTTP 404 em ${originalRequest.url}`);
        })
        .catch((importErr) => {
          console.error(
            '[API] Falha ao importar authStore pra recovery:',
            importErr,
          );
        });
    }

    return Promise.reject(error);
  },
);

export const request = {
  get: <T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => api.get<T>(url, config),

  post: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => api.post<T>(url, data, config),

  put: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => api.put<T>(url, data, config),

  patch: <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => api.patch<T>(url, data, config),

  delete: <T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> => api.delete<T>(url, config),
};

export default api;
